import React from 'react';
/**
 * Secret Display Types
 * src/components/secret/SecretDisplay/types.ts
 */

// Secret type constants
export const SECRET_TYPES = {
    MESSAGE: 'message',
    PASSWORD: 'password',
    CREDENTIALS: 'credentials',
    CUSTOM: 'custom',
    FILE: 'file',
    QR: 'qr',
    /** one of the organisation's own forms, answered by whoever created the secret */
    SCHEMA: 'schema',
} as const;

export type SecretType = (typeof SECRET_TYPES)[keyof typeof SECRET_TYPES];
export type PinType = 'static' | 'dynamic' | 'email' | 'securityKey';

/**
 * The sender's organisation, only when it is public and has verified its domain (lambda pull-secret,
 * updateViewSecret.js): a name alone could be anyone's, a verified domain cannot.
 */
export interface SenderOrganisation {
    name?: string;
    domain?: string;
}

export interface SecretDetails {
    /** the sender's verified organisation, on the answer that carries the secret */
    org?: SenderOrganisation;
    /** Download URL when file is ready, or boolean status */
    download?: string | boolean;
    expiry?: number;
    opened?: boolean;
    otl?: boolean;
    publicDescription?: string;
    secret?: string;
    /** credentials to hand to the browser extension, never to show (lambda pull-secret) */
    autofill?: { submit?: boolean };
    /** Timeout in seconds for viewing the secret */
    timeout?: string | number;
    type?: string;
    hint?: string;
    /** Filename for file-type secrets */
    filename?: string;
    emailVerification?:
        | {
              id?: string;
              hintEnabled?: boolean;
              hintValue?: string;
          }
        | boolean;
    /**
     * The form this secret was filled in on, sent with it by the backend (pull-secret,
     * updateViewSecret.js) so this page can draw it without knowing the organisation.
     */
    schema?: { main: Record<string, unknown>; uiSchema?: Record<string, unknown> };
    securityKey?: unknown[];
    keys?: unknown[];
    /** security-key challenge: returned instead of the secret while a key has to sign it */
    challenge?: string;
    allowedCredentials?: Array<{ id: string; transports?: string[] | null }>;
    pin?:
        | {
              static?: string;
              dynamic?: string;
          }
        | string;
}

// Content types for different secret types
export interface CredentialsData {
    user: string;
    password: string;
    hostname?: string;
    secret?: string; // 2FA secret
}

export interface FileData {
    filename: string;
    size: number;
    mimeType?: string;
}

export type SecretContent = string | CredentialsData | FileData | Record<string, unknown>;

// Base decrypted secret interface
export interface DecryptedSecret {
    type: SecretType;
    content: SecretContent;
    publicDescription?: string;
    privateDescription?: string;
    metadata?: Record<string, unknown>;
    /** Filename for file-type secrets */
    filename?: string;
    /** Timeout in seconds for viewing the secret */
    timeout?: number;
    /** credentials that sign the recipient in through the extension; the password is never shown */
    signIn?: { submit: boolean };
    /** the form behind a `schema` secret, so its answers are shown as the form, not as JSON */
    schema?: { main: Record<string, unknown>; uiSchema?: Record<string, unknown> };
}

// Props for the main SecretDisplay component
export interface SecretDisplayProps {
    secret: DecryptedSecret;
    onDownloadFile?: () => Promise<void>;
    onDecryptFile?: () => Promise<ArrayBuffer | null>;
}

export interface ActionButtonsProps {
    isHidden?: boolean;
    handleToggleVisibility: () => void;
    handleCopyAll: () => void;
}

// Common props shared by all secret type components
export interface BaseSecretTypeProps {
    secret: DecryptedSecret;
    isHidden?: boolean;
}

// Specific props for different secret types
export interface FileTypeProps {
    secret: DecryptedSecret;
    onDownloadFile?: () => Promise<void>;
    /** the file's decrypted bytes, for what can be played or shown in the page rather than saved */
    onDecryptFile?: () => Promise<ArrayBuffer | null>;
}

export interface QRTypeProps {
    secret: DecryptedSecret;
}

// Component map type for dynamic rendering
export type SecretComponentMap = {
    [K in SecretType]?: React.ComponentType<BaseSecretTypeProps | FileTypeProps | QRTypeProps>;
};

export type UrlPinType = PinType | 'security_key' | 'security-key' | 'security key' | 'pin' | 'dpin' | string;

// =============================================================================
// URL Parameters
// =============================================================================

export interface UrlParams {
    secretId: string;
    randomWord: string;
    pinType?: UrlPinType;
    /** the link ends in /signin: it needs the extension, which must be there before it is opened */
    signIn?: boolean;
    secretIdPart1?: string;
    secretIdPart2?: string;
}

// =============================================================================
// Email Verification
// =============================================================================

export type EmailVerificationDetails =
    | boolean
    | {
          id?: string;
          hint?: boolean;
      };

export interface EmailVerificationState {
    id?: string;
    hintEnabled: boolean;
    hintValue?: string;
}

// =============================================================================
// API Types
// =============================================================================

export type ApiStatus = 'Success' | 'Unauthorized' | 'Error' | 'NotFound' | 'Expired' | string;

export interface ApiResponse<T = SecretDetails> {
    status: ApiStatus;
    message?: string;
    details?: T;
}

export interface ViewSecretRequest {
    secretId: string;
    pin?: string;
    challengeresponse?: string;
    emailGuess?: string;
    emailVerificationCode?: string;
    /** reCAPTCHA v3, which the API scores before answering the e-mail verification step. */
    captchaToken?: string | null;
}

// =============================================================================
// Secret Status (UI State Machine)
// =============================================================================

export type SecretStatus =
    | 'start'
    | 'loading'
    | 'pin_required'
    | 'pin_email_guess'
    | 'pin_email_code'
    | 'ready'
    | 'revealed'
    | 'expired'
    | 'not_found'
    | 'already_viewed'
    /** its sender deleted the content */
    | 'erased'
    /** locked by the owner, no PIN attempts left, or not open yet (the server's message says which) */
    | 'unavailable'
    | 'error';
